import { Box, Button, IconButton, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getCarouselsError,
  getCarouselsStatus,
  getCarouselsSuccess,
  addCarouselMedia,
  updateStatus,
} from "../../../features/carouselSlice";
import { useNavigate } from "react-router-dom";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import BackButton from "../../../components/backButton";
import Header from "../../../components/Header";
import { useTranslation } from "react-i18next";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/gif", "video/mp4"];
const isVideoFile = (file) => file.type === "video/mp4";

const AddToCarousel = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { restaurantId } = useRestaurant();
  const [list, setList] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef(null);
  const status = useSelector(getCarouselsStatus);
  const error = useSelector(getCarouselsError);
  const success = useSelector(getCarouselsSuccess);
  const isSubmitting = status === "loading";

  const hasVideos = list.some(isVideoFile);
  const hasImages = list.some((file) => !isVideoFile(file));
  const hasMixedMedia = hasVideos && hasImages;

  useEffect(() => {
    if (status === "addSuccess") {
      toast.success(success);
      dispatch(updateStatus());
      navigate("/carousel");
    } else if (status === "addError") {
      toast.error(error);
    }
  }, [status, error, success, dispatch, navigate]);

  const addFiles = (incomingFiles) => {
    const validFiles = Array.from(incomingFiles).filter((file) =>
      ACCEPTED_TYPES.includes(file.type)
    );
    const rejectedCount = incomingFiles.length - validFiles.length;
    if (rejectedCount > 0) {
      toast.error(t("carousel.unsupportedFilesSkipped", { count: rejectedCount }));
    }
    if (validFiles.length > 0) {
      setList((prevList) => [...prevList, ...validFiles]);
    }
  };

  const handleFileInputChange = (event) => {
    if (event.target.files?.length) addFiles(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDraggingOver(false);
    if (event.dataTransfer.files?.length) addFiles(event.dataTransfer.files);
  };

  const handleRemoveFile = (index) => {
    setList((prevList) => prevList.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (list.length === 0 || hasMixedMedia) return;
    const formData = new FormData();
    const mediaType = isVideoFile(list[0]) ? "video" : "image";
    list.forEach((file) => formData.append("files", file));
    formData.append("mediaType", mediaType);
    dispatch(addCarouselMedia({ formData, restaurantId }));
  };

  return (
    <Box m="20px" className="main-application">
      <BackButton />
      <Header title={t("headers.addMedia")} subtitle={t("headers.addMediaSub")} />

      <Box
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={() => setIsDraggingOver(false)}
        onDrop={handleDrop}
        sx={{
          mt: 2,
          p: 4,
          border: "2px dashed",
          borderColor: isDraggingOver ? "primary.main" : "divider",
          borderRadius: 2,
          bgcolor: isDraggingOver
            ? theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.03)"
            : "transparent",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          cursor: "pointer",
          transition: "background-color 0.15s, border-color 0.15s",
          "&:hover": { borderColor: "primary.main" },
        }}
      >
        <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: "primary.main" }} />
        <Typography variant="body1" fontWeight={600}>
          {t("carousel.dropzoneHint")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("carousel.dropzoneFormats")}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.gif,.mp4"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />
      </Box>

      {list.length > 0 && (
        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("carousel.filesSelected", { count: list.length })}
          </Typography>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={isSubmitting || hasMixedMedia}
            onClick={handleSubmit}
          >
            {isSubmitting ? t("common.loading") : t("common.submit")}
          </Button>
        </Box>
      )}

      {hasMixedMedia && (
        <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
          {t("carousel.mixedMediaWarning")}
        </Typography>
      )}

      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 2,
        }}
      >
        {list.map((file, index) => {
          const isVideo = isVideoFile(file);
          const src = URL.createObjectURL(file);
          return (
            <Box
              key={`${file.name}-${index}`}
              sx={{
                position: "relative",
                borderRadius: 2,
                overflow: "hidden",
                aspectRatio: "1",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.04)"
                    : "#f7f7f7",
              }}
            >
              {isVideo ? (
                <video
                  src={src}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  muted
                />
              ) : (
                <img
                  src={src}
                  alt={file.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )}
              {isVideo && (
                <MovieOutlinedIcon
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    left: 6,
                    color: "white",
                    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
                  }}
                />
              )}
              <IconButton
                type="button"
                onClick={() => handleRemoveFile(index)}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  bgcolor: "rgba(255,255,255,0.9)",
                  width: 28,
                  height: 28,
                  "&:hover": { bgcolor: "white" },
                }}
              >
                <CloseIcon sx={{ color: "error.main", fontSize: 18 }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default AddToCarousel;
